import { useMemo } from 'react';
import { ReactComponent as CatalogIcon } from '@elementary/common-ui/assets/20x20/catalog.svg';
import { ReactComponent as LineageIcon } from '@elementary/common-ui/assets/20x20/lineage.svg';
import { ReactComponent as NewTabIcon } from '@elementary/common-ui/assets/20x20/new-tab.svg';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import { FlexCenter, IconButton, Menu } from '@elementary/common-ui/components';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { useLocation } from 'react-router-dom';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import {
  TESTS_FILTER,
  TREE_NODE,
} from '@elementary/common-ui/constants/urlParams';

type Props = {
  tableAssetId?: string;
  testName?: string;
  columnName?: string;
  showEnableDisable: boolean;
  isTestEnabled: boolean;
  onToggleDisable: () => void;
  hasEditPermission?: boolean;
};

const PanelActionsMenu = ({
  tableAssetId,
  testName,
  columnName,
  showEnableDisable,
  isTestEnabled,
  onToggleDisable,
  hasEditPermission = true,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigateWithEnv();
  const { formatUrlParam } = useSelectedTreeItem();

  const items = useMemo(() => {
    const options: Option[] = [
      {
        value: 'new-tab',
        label: 'Open in new tab',
        icon: <NewTabIcon />,
      },
      {
        value: 'lineage',
        label: 'View asset in Lineage',
        icon: <LineageIcon />,
      },
      {
        value: 'catalog',
        label: 'View asset in Catalog',
        icon: <CatalogIcon />,
      },
    ];

    if (showEnableDisable) {
      options.push({
        value: 'enable-disable',
        label: isTestEnabled ? 'Disable test' : 'Enable test',
        disabled: !hasEditPermission,
        tooltip: !hasEditPermission ? 'Requires edit permissions' : undefined,
      });
    }

    return options;
  }, [isTestEnabled, showEnableDisable, hasEditPermission]);

  const handlePageNav = (path: string, withColumn?: boolean) => {
    const newParams = new URLSearchParams();
    const columnId =
      withColumn && columnName
        ? `column.${tableAssetId}.${columnName.toLowerCase()}`
        : undefined;

    if (tableAssetId) {
      const urlParam = formatUrlParam(
        tableAssetId,
        columnId,
        columnId ? 'column' : undefined,
      );
      if (urlParam) {
        newParams.set(TREE_NODE, urlParam);
      }
    }
    if (testName && !columnId) {
      newParams.set(TESTS_FILTER, JSON.stringify([testName]));
    }
    navigate(`${path}?${newParams.toString()}`);
  };

  const handleChange = (option: string) => {
    switch (option) {
      case 'new-tab':
        navigate(`/test-triage${location.search}`, undefined, true);
        break;
      case 'lineage':
        handlePageNav('/report/lineage', true);
        break;
      case 'catalog':
        handlePageNav('/catalog');
        break;
      case 'enable-disable':
        onToggleDisable();
        break;
      default:
        break;
    }
  };

  return (
    <Menu
      menuButton={
        <IconButton>
          <FlexCenter sx={{ height: '14px', width: '14px' }}>
            <MoreIcon />
          </FlexCenter>
        </IconButton>
      }
      options={items}
      onChange={handleChange}
      closeOnChange
    />
  );
};

export default PanelActionsMenu;
